import { useState, useEffect, useCallback, useRef } from 'react';
import { getApiUrl } from '../../../../config/api';
import { chartStockWebSocketService } from '../../../../services/polygonWebSocketService';
import type { ChartStockLiveTick } from '../../../../services/chartStockLiveTypes';
import {
  computeIndexEtfScale,
  scaleEtfToIndex,
} from '../../../../services/indexChartLiveScale';
import {
  resolveSymbol,
  getChartSymbol,
  indexChartScale,
  isIndexEtfProxy,
  usesNativeIndexChart,
} from '../../../../services/symbolPricingService';
import type { GEXCandle } from '../types';
import { shouldFeedLiveMarketData } from '../../../../utils/marketSession';

const HIST_REFRESH_MS = 120_000;
const LIVE_POLL_MS = 5_000;

function intervalMs(interval: string): number {
  switch (interval) {
    case '1m': return 60_000;
    case '5m': return 5 * 60_000;
    case '15m': return 15 * 60_000;
    case '1h': return 60 * 60_000;
    case '1d': return 24 * 60 * 60_000;
    default: return 5 * 60_000;
  }
}

function bucketTs(ts: number, barMs: number): number {
  return Math.floor(ts / barMs) * barMs;
}

export type GexChartWsStatus = 'disconnected' | 'connecting' | 'connected' | 'streaming';

export function usePriceData(
  symbol: string,
  interval: string,
  chainSpot = 0,
) {
  const [candles, setCandles] = useState<GEXCandle[]>([]);
  const [liveSpot, setLiveSpot] = useState(0);
  const [etfLivePrice, setEtfLivePrice] = useState(0);
  const [wsStatus, setWsStatus] = useState<GexChartWsStatus>('disconnected');
  const [loading, setLoading] = useState(false);
  /** Why the last bar load produced nothing. Empty when the feed is healthy. */
  const [barsError, setBarsError] = useState('');
  const candleBuf = useRef<Map<number, GEXCandle>>(new Map());
  const lastTickAtRef = useRef(0);
  const prevChartScaleRef = useRef(1);
  const indexEtfScaleRef = useRef(1);
  const barMs = intervalMs(interval);

  const resolved = resolveSymbol(symbol);
  const chartSymbol = getChartSymbol(symbol);
  const nativeIndexChart = usesNativeIndexChart(resolved);
  const wsSymbol = resolved?.wsSymbol || symbol.toUpperCase();
  const indexProxy = isIndexEtfProxy(resolved);

  const chartScale =
    nativeIndexChart
      ? 1
      : indexProxy && chainSpot > 0 && etfLivePrice > 0
        ? indexChartScale(resolved, chainSpot, etfLivePrice)
        : 1;

  const indexLiveScale = nativeIndexChart
    ? computeIndexEtfScale(chainSpot || liveSpot, etfLivePrice, indexEtfScaleRef.current)
    : 1;

  const scalePrice = useCallback(
    (price: number, opts?: { indexNative?: boolean }) => {
      if (opts?.indexNative) return price;
      if (nativeIndexChart) return scaleEtfToIndex(price, indexLiveScale);
      return price * chartScale;
    },
    [nativeIndexChart, indexLiveScale, chartScale],
  );

  const flushCandles = useCallback(() => {
    const sorted = Array.from(candleBuf.current.values()).sort((a, b) => a.t - b.t);
    if (sorted.length) setCandles(sorted);
  }, []);

  const mergeTick = useCallback(
    (rawPrice: number, tsMs: number, opts?: { indexNative?: boolean }) => {
      if (rawPrice <= 0) return;
      if (!opts?.indexNative) setEtfLivePrice(rawPrice);
      const price = scalePrice(rawPrice, opts);
      setLiveSpot(price);
      const bkt = bucketTs(tsMs, barMs);
      const cur = candleBuf.current.get(bkt);
      const next: GEXCandle = cur
        ? {
            ...cur,
            h: Math.max(cur.h, price),
            l: Math.min(cur.l, price),
            c: price,
          }
        : {
            t: bkt,
            o: price,
            h: price,
            l: price,
            c: price,
            v: 0,
          };
      candleBuf.current.set(bkt, next);
      flushCandles();
      lastTickAtRef.current = Date.now();
      setWsStatus('streaming');
    },
    [barMs, scalePrice, flushCandles],
  );

  const loadHistorical = useCallback(async () => {
    if (!symbol) return;
    setLoading(true);
    try {
      const now = Date.now();
      const daysBack = interval === '1d' ? 180 : interval === '1h' ? 30 : 5;
      const from = new Date(now - daysBack * 86400_000).toISOString().slice(0, 10);
      const to = new Date(now).toISOString().slice(0, 10);
      const url = getApiUrl(
        `/api/historical/equity-bars?symbol=${encodeURIComponent(chartSymbol)}&interval=${interval}&from=${from}&to=${to}`,
      );
      const res = await fetch(url);
      if (!res.ok) {
        setBarsError(`Bars request failed (HTTP ${res.status}).`);
        return;
      }
      const json = await res.json();
      const rawBars = (json.bars || []) as any[];
      if (!rawBars.length) {
        // Keep whatever is already drawn. Clearing first meant one empty response — a rate limit,
        // a blip — wiped a working chart and replaced it with "No price data".
        setBarsError(json.message || `No OHLC bars for ${chartSymbol}.`);
        return;
      }
      setBarsError(
        json.stale ? json.message || 'Showing the last good bars.' : '',
      );
      const indexNative = nativeIndexChart;
      candleBuf.current.clear();
      for (const b of rawBars) {
        const tsMs = typeof b.t === 'number' ? b.t : new Date(b.t).getTime();
        const barO = Number(b.o) || 0;
        const barC = Number(b.c) || barO;
        if (barC <= 0) continue;
        const bkt = bucketTs(tsMs, barMs);
        candleBuf.current.set(bkt, {
          t: bkt,
          o: scalePrice(barO, { indexNative }),
          h: scalePrice(Math.max(Number(b.h) || barC, barO, barC), { indexNative }),
          l: scalePrice(Math.min(Number(b.l) || barC, barO, barC), { indexNative }),
          c: scalePrice(barC, { indexNative }),
          v: Number(b.v) || 0,
        });
      }
      flushCandles();
      const last = rawBars[rawBars.length - 1];
      const lastClose = Number(last?.c) || Number(last?.o) || 0;
      if (lastClose > 0) {
        if (!indexNative) setEtfLivePrice(lastClose);
        setLiveSpot(scalePrice(lastClose, { indexNative }));
      }
    } catch (err: unknown) {
      // The buffer is intentionally kept; only the reason is new.
      setBarsError(
        `Could not load bars: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setLoading(false);
    }
  }, [symbol, chartSymbol, interval, barMs, nativeIndexChart, scalePrice, flushCandles]);

  const pollLatestBar = useCallback(async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const url = getApiUrl(
        `/api/historical/equity-bars?symbol=${encodeURIComponent(chartSymbol)}&interval=1m&from=${today}&to=${today}`,
      );
      const res = await fetch(url);
      if (!res.ok) return;
      const json = await res.json();
      const bars = json.bars || [];
      if (!bars.length) return;
      const b = bars[bars.length - 1];
      const etf = Number(b.c) || Number(b.o) || 0;
      if (etf > 0) {
        mergeTick(etf, typeof b.t === 'number' ? b.t : Date.now(), {
          indexNative: nativeIndexChart,
        });
      }
    } catch {
      /* optional */
    }
  }, [chartSymbol, nativeIndexChart, mergeTick]);

  useEffect(() => {
    loadHistorical();
    const histTimer = setInterval(loadHistorical, HIST_REFRESH_MS);
    return () => clearInterval(histTimer);
  }, [loadHistorical]);

  useEffect(() => {
    if (!chartSymbol || !shouldFeedLiveMarketData()) {
      setWsStatus('disconnected');
      return;
    }

    const pollTimer = setInterval(pollLatestBar, LIVE_POLL_MS);
    pollLatestBar();
    const staleTimer = setInterval(() => {
      const age = Date.now() - lastTickAtRef.current;
      if (age > LIVE_POLL_MS * 2 && lastTickAtRef.current > 0) {
        setWsStatus((s) => (s === 'disconnected' ? s : 'connected'));
      }
    }, LIVE_POLL_MS);

    setWsStatus('connecting');
    const onTick = (agg: ChartStockLiveTick) => {
      const etf = agg.c ?? agg.p ?? agg.vw ?? agg.a;
      if (!etf || etf <= 0) return;
      mergeTick(etf, agg.e ?? Date.now());
    };
    chartStockWebSocketService.subscribeToStock(wsSymbol, onTick);
    chartStockWebSocketService.onConnectionChange((connected) => {
      setWsStatus((s) => (connected ? (s === 'streaming' ? s : 'connected') : 'disconnected'));
    });
    return () => {
      clearInterval(pollTimer);
      clearInterval(staleTimer);
      chartStockWebSocketService.unsubscribeFromStock(wsSymbol, onTick);
    };
  }, [chartSymbol, nativeIndexChart, wsSymbol, mergeTick, pollLatestBar]);

  useEffect(() => {
    const prev = prevChartScaleRef.current;
    if (prev === chartScale || !candleBuf.current.size) {
      prevChartScaleRef.current = chartScale;
      return;
    }
    const factor = chartScale / prev;
    if (factor !== 1) {
      for (const [k, c] of Array.from(candleBuf.current.entries())) {
        candleBuf.current.set(k, {
          ...c,
          o: c.o * factor,
          h: c.h * factor,
          l: c.l * factor,
          c: c.c * factor,
        });
      }
      flushCandles();
      setLiveSpot((p) => (p > 0 ? p * factor : p));
    }
    prevChartScaleRef.current = chartScale;
  }, [chartScale, flushCandles]);

  return {
    candles,
    loading,
    liveSpot,
    etfLivePrice,
    wsStatus,
    chartScale,
    indexProxy,
    wsSymbol,
    barsError,
    refetch: loadHistorical,
  };
}
